import { ApiService } from "./api.service";
import type {
  AiDashboardSummary,
  ReportingConversationDetail,
  ReportingConversationSummary,
} from "@/types/reporting-agent";

/** REST client for the reporting agent's persisted artifacts (cookie-authed via
 *  the shared axios instance). The live chat uses the WebSocket transport; this
 *  covers the durable reads the app needs — currently the generated dashboards. */
export class ReportingAgentService extends ApiService {
  /** All agent-generated dashboards for the workspace (owner-scoped server-side).
   *  Cursor-paginated; walk the pages so the list has the full set to merge. */
  listDashboards(slug: string): Promise<AiDashboardSummary[]> {
    return this.getAllPages<AiDashboardSummary>(({ cursor, perPage }) => {
      const qs = new URLSearchParams({ per_page: String(perPage) });
      if (cursor) qs.set("cursor", cursor);
      return `/api/workspaces/${slug}/reporting-agent/dashboards/?${qs}`;
    });
  }

  /** The inert dashboard document as an HTML string (cookie-authed). Fetched (not
   *  framed) so the API origin never serves executable LLM HTML into a browsing
   *  context; the caller renders it via an iframe `srcDoc` in an app-controlled,
   *  opaque-origin sandbox (`sandbox="allow-scripts"`, no `allow-same-origin`).
   *  `responseType: "text"` keeps axios from trying to JSON-parse the document. */
  getDashboardHtml(slug: string, artifactId: string): Promise<string> {
    return this.get<string>(
      `/api/workspaces/${slug}/reporting-agent/dashboards/${artifactId}/html/`,
      { responseType: "text" },
    );
  }

  /** The dashboard's preview screenshot as image bytes (cookie-authed). The
   *  screenshot is captured best-effort by the agent, so the endpoint 404s when a
   *  dashboard has no preview — the caller treats that (and any other failure) as
   *  "no thumbnail", never an error. `responseType: "blob"` so axios hands back
   *  the raw image instead of trying to parse it. */
  getDashboardScreenshot(slug: string, artifactId: string): Promise<Blob> {
    return this.get<Blob>(
      `/api/workspaces/${slug}/reporting-agent/dashboards/${artifactId}/screenshot/`,
      { responseType: "blob" },
    );
  }

  /** The caller's past conversations (newest-active first). Cursor-paginated. */
  listConversations(slug: string): Promise<ReportingConversationSummary[]> {
    return this.getAllPages<ReportingConversationSummary>(({ cursor, perPage }) => {
      const qs = new URLSearchParams({ per_page: String(perPage) });
      if (cursor) qs.set("cursor", cursor);
      return `/api/workspaces/${slug}/reporting-agent/conversations/?${qs}`;
    });
  }

  /** A single conversation transcript (owner-scoped server-side). */
  getConversation(slug: string, sessionId: string): Promise<ReportingConversationDetail> {
    return this.get(`/api/workspaces/${slug}/reporting-agent/conversations/${sessionId}/`);
  }
}
